import React, { useState, useEffect } from 'react';
import { KeyRound, Check, X, ChevronDown, ChevronRight, Eye, EyeOff, RefreshCw, Trash2, ShieldAlert } from 'lucide-react';
import * as adminService from '../services/adminService';

// Single-line credential inputs (tokens/keys/ids) must not contain control
// characters — newlines, CR, NUL, tabs. This mirrors the orchestrator's own
// validation for these fields, catching a copy-paste trailing \n before the
// round-trip. Multi-line textarea fields (PEM/YAML/JSON) are exempt: those are
// stored verbatim upstream and legitimately contain newlines.
const CONTROL_CHARS = /[\u0000-\u001F\u007F]/;

// Lightweight structural checks for the multi-line textarea fields, to catch
// paste errors before the round-trip. Keyed by field key; only run on a
// non-empty value and only for fields we can validate without a parser
// dependency. Returns an error string or null. Deliberately lenient so a
// valid credential is never blocked.
const TEXTAREA_VALIDATORS = {
  service_account_json: (value) => {
    try {
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return 'Expected a JSON object — paste the full service account key file.';
      }
    } catch {
      return 'Not valid JSON — paste the full service account key file.';
    }
    return null;
  },
  key_content: (value) => (
    /-----BEGIN [^-]*PRIVATE KEY-----/.test(value) && /-----END [^-]*PRIVATE KEY-----/.test(value)
      ? null
      : "Doesn't look like a PEM private key (expected -----BEGIN … PRIVATE KEY----- … -----END … PRIVATE KEY-----)."
  ),
  kubeconfig_yaml: (value) => (
    /(^|\n)\s*apiVersion\s*:/.test(value) && /(^|\n)\s*clusters\s*:/.test(value)
      ? null
      : "Doesn't look like a kubeconfig (expected apiVersion: and clusters: keys)."
  ),
};

const AdminProvidersPage = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  useEffect(() => { fetchProviders(); }, []);

  const fetchProviders = async () => {
    try {
      const res = await adminService.getProviderCredentials();
      if (res.success) {
        setProviders(res.providers || []);
      } else if (res.forbidden) {
        setForbidden(true);
      }
    } catch (err) {
      console.error('Failed to fetch providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = async (providerId) => {
    if (expandedProvider === providerId) {
      setExpandedProvider(null);
      return;
    }
    setExpandedProvider(providerId);

    // Fetch full credential values for editing
    try {
      const res = await adminService.getProviderCredential(providerId);
      if (res.success) {
        setEditData(prev => ({ ...prev, [providerId]: res.credentials || {} }));
      }
    } catch (err) {
      console.error('Failed to fetch provider details:', err);
    }
  };

  const handleFieldChange = (providerId, key, value) => {
    setEditData(prev => ({
      ...prev,
      [providerId]: { ...(prev[providerId] || {}), [key]: value },
    }));
    // Clear any prior validation/save error for this field as the user edits.
    setFieldErrors(prev => {
      const forProvider = prev[providerId];
      if (!forProvider || (!forProvider[key] && !forProvider._form)) return prev;
      const next = { ...forProvider };
      delete next[key];
      delete next._form;
      return { ...prev, [providerId]: next };
    });
  };

  // Trim single-line fields and flag control characters before submit; leave
  // multi-line textarea fields (PEM/YAML/JSON) untouched. Returns the cleaned
  // credential map plus any per-field errors.
  const sanitizeCredentials = (fields, raw) => {
    const cleaned = {};
    const errors = {};
    for (const field of fields) {
      const value = raw[field.key] ?? '';
      if (field.type === 'textarea') {
        // Preserve newlines verbatim; optionally sanity-check structure.
        cleaned[field.key] = value;
        const validator = TEXTAREA_VALIDATORS[field.key];
        if (validator && value.trim()) {
          const structuralError = validator(value);
          if (structuralError) errors[field.key] = structuralError;
        }
        continue;
      }
      const trimmed = value.trim();
      cleaned[field.key] = trimmed;
      if (CONTROL_CHARS.test(trimmed)) {
        errors[field.key] = 'Remove line breaks and control characters — this must be a single-line value.';
      }
    }
    return { cleaned, errors };
  };

  const handleSave = async (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    const { cleaned, errors } = sanitizeCredentials(provider?.fields || [], editData[providerId] || {});

    if (Object.keys(errors).length > 0) {
      setFieldErrors(prev => ({ ...prev, [providerId]: errors }));
      return;
    }
    // Reflect the trimmed values back so the user sees exactly what's submitted.
    setEditData(prev => ({ ...prev, [providerId]: cleaned }));
    setFieldErrors(prev => ({ ...prev, [providerId]: {} }));

    setSaving(providerId);
    try {
      const res = await adminService.saveProviderCredential(providerId, cleaned);
      if (res.success) {
        await fetchProviders();
        if (res.orchestrator?.error) {
          // DB saved, but the orchestrator rejected the push (e.g. 400
          // validation). Surface it inline rather than as a success toast.
          setFieldErrors(prev => ({ ...prev, [providerId]: { _form: res.orchestrator.error } }));
        } else {
          alert('Saved and pushed to orchestrator.');
        }
      } else if (res.forbidden) {
        setForbidden(true);
      } else {
        setFieldErrors(prev => ({ ...prev, [providerId]: { _form: res.error || 'Failed to save' } }));
      }
    } catch (err) {
      setFieldErrors(prev => ({ ...prev, [providerId]: { _form: 'Failed to save credentials' } }));
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (providerId) => {
    if (!window.confirm(`Remove all credentials for ${providerId}?`)) return;
    try {
      const res = await adminService.deleteProviderCredential(providerId);
      if (res.success) {
        setEditData(prev => { const next = { ...prev }; delete next[providerId]; return next; });
        setExpandedProvider(null);
        await fetchProviders();
      } else {
        alert(res.error || 'Failed to delete');
      }
    } catch (err) {
      alert('Failed to delete credentials');
    }
  };

  const toggleSecret = (providerId, key) => {
    const id = `${providerId}:${key}`;
    setShowSecrets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading provider credentials...</div>;
  }

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <ShieldAlert size={40} className="mx-auto text-gray-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Admins only</h2>
        <p className="text-sm text-gray-500">
          Provider credentials are restricted to administrators.
        </p>
      </div>
    );
  }

  const configured = providers.filter(p => p.configured);
  const unconfigured = providers.filter(p => !p.configured);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Provider Credentials</h2>
        <button onClick={fetchProviders} className="text-gray-500 hover:text-gray-700 p-2">
          <RefreshCw size={18} />
        </button>
      </div>

      <p className="text-sm text-gray-500">
        {configured.length} of {providers.length} providers configured.
        Credentials are stored in the database and pushed to the orchestrator on save.
      </p>

      {/* Configured providers */}
      {configured.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Configured</h3>
          {configured.map(p => (
            <ProviderCard
              key={p.id}
              provider={p}
              expanded={expandedProvider === p.id}
              onToggle={() => handleExpand(p.id)}
              editData={editData[p.id] || {}}
              onFieldChange={(key, val) => handleFieldChange(p.id, key, val)}
              onSave={() => handleSave(p.id)}
              onDelete={() => handleDelete(p.id)}
              saving={saving === p.id}
              showSecrets={showSecrets}
              toggleSecret={(key) => toggleSecret(p.id, key)}
              errors={fieldErrors[p.id] || {}}
            />
          ))}
        </div>
      )}

      {/* Unconfigured providers */}
      {unconfigured.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Not Configured</h3>
          {unconfigured.map(p => (
            <ProviderCard
              key={p.id}
              provider={p}
              expanded={expandedProvider === p.id}
              onToggle={() => handleExpand(p.id)}
              editData={editData[p.id] || {}}
              onFieldChange={(key, val) => handleFieldChange(p.id, key, val)}
              onSave={() => handleSave(p.id)}
              onDelete={null}
              saving={saving === p.id}
              showSecrets={showSecrets}
              toggleSecret={(key) => toggleSecret(p.id, key)}
              errors={fieldErrors[p.id] || {}}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProviderCard = ({ provider, expanded, onToggle, editData, onFieldChange, onSave, onDelete, saving, showSecrets, toggleSecret, errors = {} }) => {
  return (
    <div className="bg-white rounded-lg shadow border">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <KeyRound size={18} className={provider.configured ? 'text-green-500' : 'text-gray-300'} />
          <span className="font-medium text-gray-900">{provider.label}</span>
          <span className="text-xs text-gray-400">({provider.id})</span>
        </div>
        <div className="flex items-center space-x-2">
          {provider.configured ? (
            <span className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${provider.complete ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
              {provider.complete ? <Check size={12} /> : <X size={12} />}
              <span>{provider.complete ? 'Complete' : 'Partial'}</span>
            </span>
          ) : (
            <span className="text-xs text-gray-400">Not configured</span>
          )}
          {provider.updated_at && (
            <span className="text-xs text-gray-400">
              {new Date(provider.updated_at).toLocaleDateString()}
            </span>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t p-4 space-y-4">
          {provider.fields.map(field => {
            const secretId = `${provider.id}:${field.key}`;
            const isSecret = field.secret;
            const isVisible = showSecrets[secretId];
            const value = editData[field.key] || '';
            const fieldError = errors[field.key];

            return (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                <div className="relative">
                  {field.type === 'textarea' ? (
                    <textarea
                      value={value}
                      onChange={(e) => onFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      rows={4}
                      className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 ${fieldError ? 'border-red-400 focus:ring-red-500' : 'focus:ring-purple-500'}`}
                    />
                  ) : (
                    <input
                      type={isSecret && !isVisible ? 'password' : 'text'}
                      value={value}
                      onChange={(e) => onFieldChange(field.key, e.target.value)}
                      placeholder={field.placeholder || ''}
                      className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 pr-10 ${fieldError ? 'border-red-400 focus:ring-red-500' : 'focus:ring-purple-500'}`}
                    />
                  )}
                  {isSecret && field.type !== 'textarea' && (
                    <button
                      type="button"
                      onClick={() => toggleSecret(field.key)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  )}
                </div>
                {fieldError && (
                  <p className="mt-1 text-xs text-red-600">{fieldError}</p>
                )}
              </div>
            );
          })}

          {errors._form && (
            <p className="text-sm text-red-600">{errors._form}</p>
          )}

          <div className="flex items-center space-x-3 pt-2">
            <button
              onClick={onSave}
              disabled={saving}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save & Push to Orchestrator'}
            </button>
            {onDelete && (
              <button
                onClick={onDelete}
                className="flex items-center space-x-1 text-red-500 hover:text-red-700 text-sm px-3 py-2"
              >
                <Trash2 size={14} />
                <span>Remove</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProvidersPage;
